import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../api';
import './BalanceChart.css';

interface BalanceChartProps {
  data: { date: string; balance: number }[];
}

const PERIODS = ['1M', '3M', '6M', 'ALL'] as const;

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <span className="chart-tooltip-date">{label}</span>
        <span className="chart-tooltip-value">{formatCurrency(payload[0].value)}</span>
      </div>
    );
  }
  return null;
};

const BalanceChart: React.FC<BalanceChartProps> = ({ data: initialData }) => {
  const { t } = useTranslation();
  const [activePeriod, setActivePeriod] = useState<string>('ALL');
  const [chartData, setChartData] = useState(initialData);
  const [loading, setLoading] = useState(false);

  const handlePeriodChange = async (period: string) => {
    setActivePeriod(period);
    setLoading(true);
    try {
      const data = await api.getBalanceHistory(period);
      setChartData(data);
    } catch (err) {
      console.error('Failed to fetch balance history:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update chart when initialData changes
  React.useEffect(() => {
    setChartData(initialData);
  }, [initialData]);

  return (
    <div className="balance-chart-wrap">
      <div className="balance-chart-header">
        <h3>{t('chart.portfolioValue')}</h3>
        <div className="balance-chart-tabs">
          {PERIODS.map(period => (
            <button
              key={period}
              className={`balance-chart-tab ${activePeriod === period ? 'active' : ''}`}
              onClick={() => handlePeriodChange(period)}
              disabled={loading}
            >
              {period}
            </button>
          ))}
        </div>
      </div>
      <div className="balance-chart-body">
        {chartData.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280, color: '#a1a1aa', fontSize: '0.875rem' }}>
            {t('chart.noHistory')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="balanceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#d4a237" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#d4a237" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 11 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 11 }}
                tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`}
                dx={-10}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#d4a237"
                strokeWidth={2}
                fill="url(#balanceGrad)"
                dot={false}
                activeDot={{ r: 4, fill: '#d4a237', stroke: '#fff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BalanceChart;
